import { InlineLazyFallback } from "@/shared/RouteLoadingFallback";
import { ClockIcon, LocationPinIcon } from "@/components/assets/icons/svgs";
import ContentContainer from "@/components/containers/contentContainer";
import CustomButton from "@/shared/button/customButton";
import LazyImage from "@/shared/lazyImage";
import Popup from "@/shared/popups/popupDetails";
import { isAuthenticated } from "@/shared/services/credentialsService";
import { WorkerProfile } from "@/types/userTypes";
import { Share2 } from "lucide-react";
import { api } from "@/utils/api";
import { IconButton, Pagination, Rating } from "@mui/material";
import moment from "moment";
import { lazy, Suspense, useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "@/utils/appToast";
import { useTranslation } from 'react-i18next';
import { AvailabilityOptions } from "@/shared/utils/enum";
import { decodeId } from "@/utils/idEncoder";
import {
  getProviderUserId,
  getWorkerProfileId,
} from "@/utils/workerProfileUtils";
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";
import { trackMetaViewContent } from "@/utils/metaPixel";
const RequestQuote = lazy(() => import("@/pages/hire/request-quote"));
const SendProposal = lazy(() => import("@/pages/hire/send-proposal"));



type WorkerServiceDetailsProps = {
  initialWorker?: WorkerProfile | null;
  initialReviews?: any[];
};

/** Stable default — `initialReviews = []` creates a new array every render and retriggers effects. */
const EMPTY_REVIEWS: any[] = [];

function profileMatchesWorkerId(
  profile: WorkerProfile | null | undefined,
  id: string | undefined
) {
  if (!profile || !id) return false;
  return [profile.id, profile._id].some(
    (value) => value != null && String(value) === String(id)
  );
}

const WorkerServiceDetails = ({
  initialWorker = null,
  initialReviews = EMPTY_REVIEWS,
}: WorkerServiceDetailsProps) => {
  const { slug } = useParams()

  const shortId = slug?.split("-").pop();
  const workerId = shortId ? decodeId(shortId) : undefined;
  const canUseInitial = profileMatchesWorkerId(initialWorker, workerId);

  const [proposalPopupShow, setProposalPopupToggle] = useState(false);
  const { user } = useSelector((state: { user }) => state.user || {});
  const [messageText, setMessageText] = useState('');
  const [reviews, setReviews] = useState<any[]>(canUseInitial ? initialReviews : EMPTY_REVIEWS);
  const [reviewData ,setReviewData] = useState<any>({})
  const [requestQuotePopup, setRequestQuotePopup] = useState(false);
  const [data, setData] = useState<WorkerProfile | null>(canUseInitial ? initialWorker : null);
   const [offset, setOffset] = useState<number>(1);
    const [pageSize, setPageSize] = useState<number>(4);
  const [isLoading, setIsLoading] = useState(!canUseInitial);
  const { t } = useTranslation();
  const navigate = useNavigate();
  const fetchedForWorkerId = useRef<string | null>(null);
  const jobExistCheckedFor = useRef<string | null>(null);
  const initialWorkerRef = useRef(initialWorker);
  const initialReviewsRef = useRef(initialReviews);
  initialWorkerRef.current = initialWorker;
  initialReviewsRef.current = initialReviews;
  const handleShareProfile = async () => {
    const shareUrl = window.location.href; // current full URL
    const shareText = `${data?.user?.first_name || ''} ${data?.user?.last_name || ''} - ${t('checkOutThisWorkerProfile')}`;

    try {
      if (navigator.share) {
        // 📱 Native share dialog (mobile)
        await navigator.share({
          title: document.title,
          text: shareText,
          url: shareUrl,
        });
      } else {
        // 💻 Fallback: copy to clipboard
        await navigator.clipboard.writeText(shareUrl);
        toast.success(t('linkCopiedToClipboard') || 'Profile link copied to clipboard!');
      }
    } catch (err) {
      console.error('Error sharing profile:', err);
      toast.error(t('couldNotShareProfile') || 'Could not share the profile.');
    }
  };

  const fetchWorkerReviews = (reviewWorkerId: string, page = offset) => {
    if (!reviewWorkerId) return;
    api.getAllWorkerReviews(reviewWorkerId, page, pageSize).then((res) => {
      setReviewData(res?.data)
      if (res?.data) {
        const nextReviews = res?.data?.data?.filter((review) => review?.rating);
        setReviews(nextReviews);
      }
      else {
        setReviews([]);
      }
    }).catch((err) => {
      console.log("error", err)
    })
  }

  // Home list scroll must not carry over onto the profile
  useEffect(() => {
    window.scrollTo(0, 0);
    document.documentElement.scrollTop = 0;
    document.body.scrollTop = 0;
  }, [slug]);

  // Meta Pixel ViewContent when a worker profile is available
  useEffect(() => {
    if (!data?.user?._id) return;
    const name = `${data.user.first_name || ""} ${data.user.last_name || ""}`.trim();
    trackMetaViewContent({
      content_name: name || "worker_profile",
      content_ids: String(data.user._id),
      content_type: "product",
      content_category: "worker_profile",
    });
  }, [data?.user?._id]);

  // Load per workerId/slug. SSR may provide first paint; client revalidates once per worker.
  useEffect(() => {
    let cancelled = false;

    if (!workerId) {
      navigate("/");
      return;
    }

    setOffset(1);
    setMessageText("");
    setProposalPopupToggle(false);
    setRequestQuotePopup(false);
    jobExistCheckedFor.current = null;

    const initWorker = initialWorkerRef.current;
    const initReviews = initialReviewsRef.current || EMPTY_REVIEWS;
    const useInitial = profileMatchesWorkerId(initWorker, workerId);
    const fetchKey = workerId;

    // First paint from SSR when available (SEO / no blank flash)
    if (useInitial && initWorker && fetchedForWorkerId.current !== fetchKey) {
      setData(initWorker);
      setReviews(initReviews);
      setIsLoading(false);
      if (!initReviews?.length && initWorker?.user?._id) {
        fetchWorkerReviews(initWorker.user._id, 1);
      }
    }

    // Already have this worker
    if (fetchedForWorkerId.current === fetchKey) {
      return;
    }

    // No SSR payload → show loading; otherwise keep SSR data while revalidating
    if (!useInitial || !initWorker) {
      setData(null);
      setReviews([]);
      setReviewData({});
      setIsLoading(true);
    }

    (async () => {
      try {
        const res = await api.getWorkerById(workerId);
        if (cancelled) return;
        fetchedForWorkerId.current = fetchKey;
        setData(res);
        setIsLoading(false);
        if (res?.user?._id) {
          fetchWorkerReviews(res.user._id, 1);
        }
        if (user?._id && res?.user?._id) {
          const key = `${user._id}:${res.user._id}`;
          jobExistCheckedFor.current = key;
          void api.isJobExist(user._id, res.user._id);
        }
      } catch (err) {
        if (!cancelled) {
          console.log("error", err);
          setIsLoading(false);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workerId, slug]);

  // If user logs in after profile is already shown, check job once
  useEffect(() => {
    if (!user?._id || !data?.user?._id) return;
    const key = `${user._id}:${data.user._id}`;
    if (jobExistCheckedFor.current === key) return;
    jobExistCheckedFor.current = key;
    void api.isJobExist(user._id, data.user._id);
  }, [user?._id, data?.user?._id])

  const handleRequestQuotePopup = () => {
    if (!isAuthenticated()) {
      return window.location.assign("/ingresar");
    }

    setRequestQuotePopup(true)
  }

  const handleChangePage = (event: unknown, newPage: number) => {
    setOffset(newPage);
}

  // Pagination only — page 1 is loaded in the slug effect above (avoids duplicate fetch)
  const skipInitialReviewsEffect = useRef(true);
  useEffect(()=>{
    if (skipInitialReviewsEffect.current) {
      skipInitialReviewsEffect.current = false;
      return;
    }
    if (data?.user?._id) {
      fetchWorkerReviews(data.user._id, offset);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  },[offset])


  const handleSendMessage = () => {

    if (!isAuthenticated()) {
      return toast.error("Debes iniciar sesión para enviar un mensaje.");
    }
    if (user?._id === data?.user?._id) {
      return toast.error("No puedes enviarte un mensaje a ti mismo.");
    }
    setIsLoading(true);
    api.sendMessageToWorker(data.user?._id, messageText).then((res) => {
      console.log("response", res)
      toast.success("Mensaje enviado correctamente.");
      setMessageText('');
      setIsLoading(false);
    }).catch((err) => {
      console.log("error", err)
      setIsLoading(false);
    })

  };





  if (isLoading || !data) {
    return (
      <div className="p-6 mt-20">
        <div className="flex items-center gap-4">
          <Skeleton circle height={100} width={100} />
          <div className="flex-1">
            <Skeleton height={20} width="50%" />
            <Skeleton height={15} width="30%" />
          </div>
        </div>
        <div className="mt-8">
          <Skeleton count={6} height={15} />
        </div>
      </div>
    );
  }

  return (
    <ContentContainer className="mt-12 p-0">
      {/* Request Quote Popup */}
      <Popup

        size={'sm'}
        className="bg-darkBlue"
        headerTitle=""
        contentSx={{ padding: 0 }}
        isOpen={requestQuotePopup}
        handleSubmit={() => { }}
        handleClose={() => setRequestQuotePopup(false)}
        slotProps={{ preventOutsideClose: true }}
        bodyChildren={
          requestQuotePopup ? (
            <Suspense fallback={<InlineLazyFallback />}>
              <RequestQuote
                workerid={getProviderUserId(data) || String(data?.user?._id || "")}
                workerProfileId={getWorkerProfileId(data) || undefined}
                providerIds={
                  getProviderUserId(data)
                    ? [getProviderUserId(data) as string]
                    : undefined
                }
                onSuccess={() => setRequestQuotePopup(false)}
              />
            </Suspense>
          ) : null
        }
      />

      <Popup
        size={'sm'}
        className="bg-darkBlue"
        headerTitle=""
        contentSx={{ padding: 0 }}
        isOpen={proposalPopupShow}
        handleSubmit={() => { }}
        handleClose={() => setProposalPopupToggle(false)}
        slotProps={{
          root: { sx: { zIndex: 4000 } }
        }}
        bodyChildren={
          proposalPopupShow ? (
            <Suspense fallback={<InlineLazyFallback />}>
              <SendProposal workerid={String(data?.user?._id)} workerProfileId={String(data?.user?.workerProfile)} onSuccess={() => setProposalPopupToggle(false)} jobUserID={""} jobtype={""} jobId={""} />
            </Suspense>
          ) : null
        }
      />
      {/* <hr className="w-full relative border-solid border  border-[#CCCCCC] my-4" /> */}
      <div className="flex flex-col sm:gap-36 gap-60">


        <div className="rounded-xl h-full w-full relative">
          <div className="sm:h-[200px] w-full h-[150px] rounded-lg bg-darkBlue"  />
          <div className="flex flex-col sm:flex-row gap-4 sm:items-end items-start absolute sm:-bottom-[80px] -bottom-[210px]  sm:left-10  left-0 w-full ">
            <div className="sm:w-[190px] sm:h-[170px] w-[100px] h-[100px] rounded-full border-2 border-solid border-white">


              <img className='rounded-full   object-cover w-full h-full' src={data?.user?.avatar || '/images/resource/user-placeholder.png'} alt='' />
            </div>
            <div className="flex flex-col sm:flex-row justify-between w-full gap-1">
              <div className="w-full">


                <h3 className="text-lg font-semibold">
                  <p className="text-2xl font-bold text-black">
                    {data?.user?.first_name + " " + data?.user?.last_name}
                  </p>
                </h3>

                <p className="flex items-center text-black">
                  <Rating
                    name="minimum-rating"
                    value={1}
                    max={1}
                    readOnly
                    sx={{ color: '#FFE234 !important' }}
                  />
                  <span className="font-medium text-sm"> {data?.averageRating ? Number(data?.averageRating)?.toFixed(1) : 0} ({t('reviews')})</span>
                </p>
              </div>
              {user && user?.type !== 'worker' && (
                <div
                  className="
      flex flex-col sm:flex-row sm:items-center items-stretch
      sm:justify-end justify-center gap-3 sm:gap-4
      sm:w-1/3 w-full sm:mr-10 mr-0 mt-3 sm:mt-0
    "
                >
                  {/* Buttons container */}
                  <div
                    className="
        flex sm:flex-row flex-col
        gap-3 w-full sm:w-auto
      "
                  >
                    {/* <CustomButton
        handleButtonClick={handleProposalPopup}
        label={t('sendProposal')}
        className="
          !rounded-3xl !bg-darkBlue border border-darkBlue
          px-6 py-2 flex-1 sm:w-auto
          h-10 flex items-center justify-center
          whitespace-nowrap
        "
        labelStyle="text-sm text-white normal-case leading-none"
      /> */}

                    <CustomButton
                      handleButtonClick={() => handleRequestQuotePopup()}
                      label={t('RequestQuote')}
                      className="
          !rounded-3xl border border-darkBlue
          bg-opacity-10 border-opacity-40 bg-blue-100
          px-6 py-2 flex-1 sm:w-auto
          h-10 flex items-center justify-center
          whitespace-nowrap
        "
                      labelStyle="text-sm text-darkBlue normal-case leading-none"
                    />
                  </div>

                  {/* Share button */}
                  <div className="flex justify-center sm:justify-end">
                    <IconButton
                      onClick={handleShareProfile}
                      className="
          !rounded-full border border-darkBlue
          bg-white text-darkBlue
          hover:bg-darkBlue hover:text-white
          transition-all duration-200
        "
                    >
                      <Share2 size={18} />
                    </IconButton>
                  </div>
                </div>
              )}

            </div>
          </div>
        </div>

        <div className="flex flex-col sm:flex-row gap-6 justify-between">
          <div className="flex flex-col gap-4 w-full">
            <h1 className="text-2xl font-semibold capitalize">{t('profileDetails')}</h1>
            <div className=" flex flex-col gap-4 border rounded-3xl border-[#CCCCCC] p-6 bg-white">
              <div className="flex sm:flex-nowrap flex-wrap gap-3 w-full">
                {data?.hourlyPay ? <CustomButton label={`$${data?.hourlyPay || ''}/${t('hourly')}`} className="!rounded-md border bg-opacity-10 border-darkBlue border-opacity-40  bg-blue-100 w-full h-12" labelStyle="text-md text-black font-medium" /> : ''}
                <CustomButton
                  icon={<LocationPinIcon className="mr-2" />}
                  label={data?.locationName || t('location')}
                  title={data?.locationName || t('location')}
                  className="!rounded-md border bg-opacity-10 border-darkBlue border-opacity-40 bg-blue-100 w-full h-12"
                  labelStyle="text-md text-black font-medium text-left truncate"
                />

                <CustomButton icon={<ClockIcon className="mr-2" />} label={data?.createdAt ? moment(data?.createdAt).format('MMMM YYYY') : ''} className="!rounded-md border bg-opacity-10 border-darkBlue border-opacity-40  bg-blue-100 w-full h-12" labelStyle="text-md text-black font-medium" />

              </div>
              <hr className="w-full relative border-solid border  border-[#CCCCCC] my-4" />

              <div className="flex flex-col gap-4">
                <h1 className="text-xl font-semibold capitalize">{t('servicesOffered')}:</h1>

                <div className="flex flex-wrap gap-3">
                  {data?.servicesOffered?.map((service) => (
                    <CustomButton
                      key={service._id}
                      label={service.name}
                      title={service.name}
                      className="!rounded-md border bg-opacity-10 border-darkBlue border-opacity-40 bg-blue-100 px-4 sm:px-6 min-h-12 h-auto py-2 max-w-full !normal-case !whitespace-normal !items-start"
                      labelStyle="text-md text-black font-medium text-left whitespace-normal break-words max-w-full"
                    />
                  ))}
                </div>
              </div>

              <hr className="w-full relative border-solid border  border-[#CCCCCC] my-4" />

              <div className="flex flex-col gap-4">
                <h1 className="text-xl font-semibold capitalize">{t('Regions')}:</h1>

                <div className="flex flex-wrap gap-3">
                  {data?.Regions?.map((region) => (
                    <CustomButton
                      key={region._id}
                      label={region.name}
                      title={region.name}
                      className="!rounded-md border bg-opacity-10 border-darkBlue border-opacity-40 bg-blue-100 px-4 sm:px-6 min-h-12 h-auto py-2 max-w-full !normal-case !whitespace-normal !items-start"
                      labelStyle="text-md text-black font-medium text-left whitespace-normal break-words max-w-full"
                    />
                  ))}
                </div>
              </div>
              <hr className="w-full relative border-solid border  border-[#CCCCCC] my-4" />

              <div className="flex flex-col gap-4">
                <h1 className="text-xl font-semibold capitalize">{t('description')}:</h1>
                <p className="text-sm">{data?.description}</p>
              </div>

              <hr className="w-full relative border-solid border  border-[#CCCCCC] my-4" />

              <div className="flex flex-col gap-4">
                <h1 className="text-xl font-semibold">{t('availability')}:</h1>
                <p className="text-sm">
                  {data?.availability === AvailabilityOptions?.mondayToFriday ? t('mondayToFriday') : null}
                  {data?.availability === AvailabilityOptions?.saturdayToSunday ? t('saturdayToSunday') : null}
                  {data?.availability === AvailabilityOptions?.allDays ? t('allDays') : null}
                </p>
              </div>

            </div>
          </div>

          <div className="flex flex-col gap-4 sm:w-1/3 w-full">
            <h1 className="text-2xl font-semibold">{t('ratingsAndReviews')}</h1>
            <div className=" flex flex-col  border rounded-3xl border-[#CCCCCC] p-4 bg-white">
              <div className="flex gap-3 justify-between">
                <div className="flex flex-col gap-2 w-full">
                  {reviews?.length > 0 ? (
                    <>
                      <h1 className="text-3xl font-semibold">
                        {data?.averageRating ? Number(data?.averageRating).toFixed(1) : 0}
                      </h1>
                      <Rating
                        name="rating"
                        value={Number(data?.averageRating || 0)}
                        max={5}
                        precision={0.5}
                        readOnly
                        sx={{ color: '#FFE234 !important' }}
                      />
                      <p className="text-sm font-medium text-black">
                        ({reviews?.length}) {t('ratingsAndReviewsSubtitle')}
                      </p>
                    </>
                  ) : (
                    <p className="text-lg font-medium text-gray-500 flex justify-center">{t('noReviewsYet')}</p>
                  )}
                </div>


                {/* <div className="w-full h-full">
                                    <LazyImage className="h-full w-full rounded-lg" src="/images/background/ratings_reviews.svg" alt="about" />
                                </div> */}


              </div>
              <hr className="w-full relative border-solid border  border-[#CCCCCC] my-1" />

              {reviews?.length > 0 && reviews?.map((review, index) => (
                <div key={review?._id}>
                  <div className="flex flex-col gap-4">
                    <div className="flex justify-between">
                      <Rating
                        name="rating"
                        value={Number(review?.rating || 0)}
                        max={5}
                        precision={0.5}
                        readOnly
                        sx={{ color: '#FFE234 !important' }}
                      />
                      <p className="text-base text-black font-normal">{Number(review?.rating)?.toFixed(1) || 0} ({t('review')})</p>
                    </div>
                    <div className="flex justify-between">
                      <h1 className=" capitalize text-base text-black">{review?.job?.user?.fullName}</h1>
                      <span className="text-xs text-black font-normal">{moment(review?.reviewDate).format('DD MMM YYYY')}</span>
                    </div>
                    <p className="text-sm font-normal text-[#696969]"> "{review?.review || t('noReviewsYet')}"</p>
                  </div>
                  {index !== reviews?.length - 1 && <hr className="w-full relative border-solid border  border-[#CCCCCC] my-4" />}
                </div>
              ))}
              
             {(reviewData?.totalPages > 0) && <Pagination
                count={reviewData?.totalPages}
                color="primary"
                variant="outlined"
                page={offset}
                onChange={handleChangePage}
                showFirstButton
                showLastButton
                siblingCount={0}
              />}
            </div>
          </div>

        </div>


      </div>
    </ContentContainer>
  )
}

/** Remount on slug change so a previous profile cannot leak into the next one. */
export default function WorkerServiceDetailsPage(props: WorkerServiceDetailsProps) {
  const { slug } = useParams();
  return <WorkerServiceDetails key={slug || "worker"} {...props} />;
}